import { useMutation, useQueryClient } from '@tanstack/react-query';

import { NIL_UUID } from '../constants';
import { IWebhookConfig, IWebhookConfigUpsert } from '../types';
import useImperativeRequestWrapper from '@/hooks/useImperativeRequestWrapper';
import { MutationConfig } from '@/lib/react-query';

type ImperativeRequestFn = ReturnType<typeof useImperativeRequestWrapper>[1];

interface IUpsertWebhookConfigArgs {
  configId?: string;
  values: IWebhookConfigUpsert;
}

const upsertWebhookConfig =
  ({
    partnerId,
    makeRequest,
  }: {
    partnerId: string;
    makeRequest: ImperativeRequestFn;
  }) =>
  async ({
    configId,
    values,
  }: IUpsertWebhookConfigArgs): Promise<IWebhookConfig> => {
    const {
      // Remove fields that are not needed for the API request
      created_at: _createdAt,
      updated_at: _updatedAt,
      ...filteredConfig
    } = values;

    const { data, error } = await makeRequest({
      path: `/partners/${partnerId}/webhooks/configs/${configId ?? NIL_UUID}`,
      method: 'post',
      body: filteredConfig,
    });
    if (error || !data) throw new Error(error?.message ?? 'Upsert failed');

    return data.config;
  };

type UseUpsertWebhookConfigOptions = {
  partnerId: string;
  config?: MutationConfig<ReturnType<typeof upsertWebhookConfig>>;
};

export const useUpsertWebhookConfig = ({
  partnerId,
  config,
}: UseUpsertWebhookConfigOptions) => {
  const [, makeRequest] = useImperativeRequestWrapper('lpVillageV3');
  const queryClient = useQueryClient();

  return useMutation({
    ...config,
    mutationFn: upsertWebhookConfig({ partnerId, makeRequest }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['webhook-configs', partnerId],
      });
    },
  });
};
